import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useUpdateShop from "@babackapp/private-api-client/client/use-update-shop";
import SettingsFormContainer, {
  SettingsFormHeader,
  SettingsFormPageButtonBar,
  SettingsFormSectionHeader,
} from "@babackapp/react-hook-forms-components/form-page";
import useTranslation from "@babackapp/translations/use-translation";
import { Form } from "@babackapp/ui/components/form";
import { useForm } from "react-hook-form";
import ReturnToStoresCard from "../return-to-store/return-to-store-card";
import TeamMembersCard from "../team-members/team-members-card";
import WorkflowLogisiticianCard from "../workflow-logisitican/workflow-logistician-card";
import ShopSettingsContactForm from "./shop-settings-contact-form";
import ShopSettingsInformationForm from "./shop-settings-information-form";
import { ShopSettingsFormValues } from "./types";
import { DAY_MS } from "@/lib/utils/date.ts";

function ShopSettingsForm() {
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateShop } = useUpdateShop(shop.id);

  const form = useForm<ShopSettingsFormValues>({
    defaultValues: {
      name: shop.name,
      vatNumber: shop.vatNumber,
      email: shop.email,
      phone: shop.phone,
      /**
       * From milliseconds to days
       */
      timeDelay: shop.timeDelay / DAY_MS,
      giftCode: {
        activated: shop.giftCode.activated || false,
      },
      // oneResolutionPerReturn: shop.oneResolutionPerReturn || false,
      logoId: {
        id: shop.logoId || "",
        url: shop.logoUrl,
      },
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await updateShop({
      email: values.email,
      name: values.name,
      phone: values.phone,
      vatNumber: values.vatNumber,
      /**
       * From days to milliseconds
       */
      timeDelay: values.timeDelay * DAY_MS,
      giftCode: {
        activated: values.giftCode.activated,
      },
    });

    /**
     * Reset the dirty state of the form after
     * successful submission
     */
    form.reset(values);
  });

  return (
    <div className="w-full pb-2 flex justify-start relative">
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col w-full">
          <div className="flex flex-col gap-8">
            <ShopSettingsInformationForm />
            <ShopSettingsContactForm />
          </div>
          <SettingsFormPageButtonBar />
        </form>
      </Form>
    </div>
  );
}

export default function ShopSettings() {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();

  return (
    <SettingsFormContainer
      navigation={[
        {
          title: t("settings.shop.sections.basic.title"),
          id: "information",
        },
        {
          title: t("settings.shop.sections.contact.title"),
          id: "contact",
        },
        {
          title: t("settings.config.workflowLogistician.name"),
          id: "workflow-logistician",
        },
        {
          title: t("settings.team.title"),
          id: "team",
        },
      ]}
    >
      <SettingsFormHeader
        title={t("settings.shop.title")}
        description={t("settings.shop.subtitle")}
      />
      <ShopSettingsForm />
      <SettingsFormSectionHeader
        title={t("settings.config.workflowLogistician.name")}
        id="workflow-logistician"
      />
      <WorkflowLogisiticianCard />

      {shop.plan.returnToStore && (
        <>
          <SettingsFormSectionHeader
            title={t("settings.shop.physic.title")}
            id="stores"
          />
          <ReturnToStoresCard />
        </>
      )}

      <SettingsFormSectionHeader title={t("settings.team.title")} id="team" />
      <TeamMembersCard />
    </SettingsFormContainer>
  );
}
